import { useEffect, useRef, useState, type ReactNode } from "react";
import { isNativeApp } from "../lib/platform";
import { useI18n } from "../lib/i18n";
import { refreshInteractionBlocked, usePullGesture } from "../lib/usePullGesture";
import { PullRefreshIndicator } from "./beui/PullRefreshIndicator";

const REFRESH_DEADLINE_MS = 20_000;

/** Only explicitly safe, caller-owned recovery messages pass through the shared boundary. */
export class RefreshError extends Error {}

type PullToRefreshProps = {
  children: ReactNode;
  identity: string;
  disabled?: boolean;
  className?: string;
  /** Await all reads and check signal.aborted before committing results, including after timeout. */
  onRefresh: (signal: AbortSignal) => Promise<void>;
};
type Feedback = { identity: string; status: "busy" | "success" | "error"; message: string };
type Attempt = { identity: string; controller: AbortController; timer: ReturnType<typeof setTimeout> };

/** Native-only data refresh; browser callers retain exactly their original children. */
export function PullToRefresh(props: PullToRefreshProps) {
  return isNativeApp() ? <NativePullToRefresh {...props} /> : <>{props.children}</>;
}

function NativePullToRefresh({ children, identity, disabled = false, onRefresh, className = "" }: PullToRefreshProps) {
  const { t } = useI18n();
  const root = useRef<HTMLDivElement>(null);
  const attempt = useRef<Attempt | null>(null);
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [distance, setDistance] = useState(0);
  const visibleFeedback = feedback?.identity === identity ? feedback : null;
  const busy = visibleFeedback?.status === "busy";

  useEffect(() => {
    // Returning to a prior view/mutation state must not revive its old feedback.
    setFeedback(null);
    return () => {
      const previous = attempt.current;
      if (!previous || previous.identity !== identity) return;
      clearTimeout(previous.timer);
      previous.controller.abort();
      attempt.current = null;
    };
  }, [identity]);

  useEffect(() => {
    // An editor or overlay opened during a read acquires the interaction just
    // as it does before a pull. Cancel before its draft can be replaced.
    const interrupt = () => {
      const active = attempt.current;
      if (!active || !refreshInteractionBlocked()) return;
      clearTimeout(active.timer);
      active.controller.abort();
      attempt.current = null;
      setFeedback(null);
    };
    document.addEventListener("focusin", interrupt);
    const observer = new MutationObserver(interrupt);
    observer.observe(document.body, { subtree: true, childList: true, attributes: true,
      attributeFilter: ["open", "aria-modal", "aria-expanded", "aria-hidden", "data-refresh-mutation"] });
    return () => { document.removeEventListener("focusin", interrupt); observer.disconnect(); };
  }, []);

  async function refresh() {
    if (attempt.current || disabled || refreshInteractionBlocked()) return;
    const controller = new AbortController();
    const active: Attempt = { identity, controller, timer: setTimeout(() => {
      if (attempt.current !== active) return;
      // Aborting invalidates owner-side commits before another refresh can start.
      controller.abort();
      attempt.current = null;
      if (currentIdentity.current === identity) setFeedback({ identity, status: "error", message: t("refresh.timeout") });
    }, REFRESH_DEADLINE_MS) };
    attempt.current = active;
    setFeedback({ identity, status: "busy", message: t("refresh.loading") });
    try {
      await onRefresh(controller.signal);
      if (attempt.current === active && currentIdentity.current === identity && !controller.signal.aborted) {
        setFeedback({ identity, status: "success", message: t("refresh.updated") });
      }
    } catch (failure: unknown) {
      if (attempt.current === active && currentIdentity.current === identity && !controller.signal.aborted) {
        setFeedback({ identity, status: "error", message: failure instanceof RefreshError ? failure.message : t("refresh.error") });
      }
    } finally {
      clearTimeout(active.timer);
      if (attempt.current === active) attempt.current = null;
    }
  }

  const gesture = usePullGesture(root, disabled || busy, () => { void refresh(); }, identity);
  gesture.progress.current = setDistance;
  const pullText = distance >= gesture.pullDistance ? t("refresh.release") : t("refresh.pull");

  return <div ref={root} data-pull-to-refresh-root className={`min-w-0 ${className}`} style={{ WebkitTouchCallout: "none" }}
    aria-busy={busy} data-disabled={disabled || busy || undefined}
    data-state={busy ? "refreshing" : distance >= gesture.pullDistance ? "ready" : distance > 0 ? "pulling" : "idle"}>
    <PullRefreshIndicator key={identity} distance={distance} threshold={gesture.pullDistance} refreshing={busy}
      label={busy ? t("refresh.loading") : pullText} />
    {visibleFeedback?.status === "error" && <div role="alert" className="container-x py-2 text-sm text-navy">{visibleFeedback.message}</div>}
    <span role="status" aria-atomic="true" className="sr-only">{visibleFeedback?.status === "success" ? visibleFeedback.message : null}</span>
    {children}
  </div>;
}
